import { useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { useStore } from "@/app/store";
import {
  GET_OPERATIONAL_DATA,
  type OpDataFieldEntry,
} from "@/pages/OperationalData/graphql/queries";
import type { PlanningMonth } from "../components/CostPanel/CostPanel.types";
import {
  resolveOpDataVariableValues,
  type OpDataVariableCells,
} from "./resolveOpDataVariableValues";
import type { OpDataVariableOption } from "./useOpDataCatalog";

// Per-month values for the referenced Operational Data variables, from the calc's
// frozen opDataSnapshot (falling back to a live operationalData query when it has never
// been computed). One planning year. Kept out of InputsBody so its Apollo dependency
// only mounts when there are variables to show.
const useOpDataVariableValues = (
  line: string | null,
  referencedOpDataVariables: OpDataVariableOption[],
  planningMonths: PlanningMonth[],
): OpDataVariableCells[] => {
  const client = useApolloClient();
  const snapshotFields = useStore(
    (s) => s.calculation?.opDataSnapshot?.fields ?? null,
  );
  const [liveFields, setLiveFields] = useState<OpDataFieldEntry[]>([]);

  const year = planningMonths[0]
    ? Number(planningMonths[0].key.split("-")[0])
    : null;

  const needsLiveFallback =
    snapshotFields === null &&
    line !== null &&
    year !== null &&
    referencedOpDataVariables.length > 0;

  useEffect(() => {
    if (!needsLiveFallback) return;
    let cancelled = false;

    void client
      .query({
        query: GET_OPERATIONAL_DATA,
        variables: { line, year },
        fetchPolicy: "cache-first",
      })
      .then((res) => {
        if (!cancelled) setLiveFields(res.data?.operationalData?.fields ?? []);
      })
      .catch(() => {
        if (!cancelled) setLiveFields([]);
      });

    return () => {
      cancelled = true;
    };
  }, [client, line, year, needsLiveFallback]);

  const fields = snapshotFields ?? liveFields;

  return useMemo(
    () =>
      resolveOpDataVariableValues(
        referencedOpDataVariables,
        planningMonths,
        fields,
      ),
    [referencedOpDataVariables, planningMonths, fields],
  );
};

export default useOpDataVariableValues;
